import { createApi } from '@reduxjs/toolkit/query/react'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'
import { getUserId } from 'utils/common'

import {
  InvoiceResponse,
  InvoiceType,
  ListInvoiceParams,
  TransformInvoiceResponse,
  Event,
  DeleteEventPayload,
  AddDocumentPayload,
  GetPreSignedUrlResponse,
  GetPreSignedUrlPayload,
  SendInvoiceEmailPayload,
  ListDocumentsResponse,
  AddDocumentResponse,
  InvoiceEmailDto,
  CreateBatchPayload,
  BatchResponse,
  SendBatchInvoiceEmailPayload,
  UpdateInvoiceStatusPayload,
  CarrierInvoice,
  CarrierPayableStatus,
} from './invoices.types'
import {
  transformListInvoiceDocuments,
  transformListInvoices,
} from './invoices.api.utils'

const baseUrl = `${process.env.REACT_APP_ORDER_SERVICE_API}`

/** Status sent to GET .../invoices/{userId}/status */
export function getListInvoicesApiStatus(
  invoiceType: InvoiceType
): 'TO_BE_INVOICED' | 'NOT_PAID' | 'PAID' {
  if (invoiceType === 'TO_DELIVER' || invoiceType === 'TO_INVOICE') {
    return 'TO_BE_INVOICED'
  }
  return invoiceType
}

export const invoicesApi = createApi({
  reducerPath: 'invoicesApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  tagTypes: ['Invoices', 'Invoice', 'PaymentDocuments', 'CarrierInvoices'],
  endpoints: (builder) => ({
    listInvoicesByUser: builder.query<
      TransformInvoiceResponse[],
      ListInvoiceParams
    >({
      query: ({ invoiceType }) => ({
        // Receivables workspace: always request invoiceType=receivables (null → receivables on API)
        url: `oapi/v1/invoices/${getUserId()}/status?status=${getListInvoicesApiStatus(
          invoiceType
        )}&invoiceType=receivables`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const status = getListInvoicesApiStatus(queryArgs.invoiceType)
        const customerId = queryArgs.customerId ?? ''
        return `${endpointName}(${getUserId()},${status},receivables,${customerId})`
      },
      transformResponse: (
        response: InvoiceResponse[],
        _meta,
        arg: ListInvoiceParams
      ) => transformListInvoices(response, arg?.customerId || ''),
      providesTags: ['Invoices'],
    }),

    listCarrierInvoicesByStatus: builder.query<
      CarrierInvoice[],
      { status: CarrierPayableStatus }
    >({
      query: ({ status }) => ({
        url: `oapi/v1/invoices/carrier/${getUserId()}/status?status=${status}`,
      }),
      providesTags: ['CarrierInvoices'],
    }),

    getCarrierInvoiceById: builder.query<CarrierInvoice, { id: string }>({
      query: ({ id }) => ({
        url: `oapi/v1/invoices/carrier/by-id/${id}`,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'CarrierInvoices', id },
      ],
    }),

    getInvoiceById: builder.query<InvoiceResponse, { id: string }>({
      query: ({ id }) => ({
        url: `oapi/v1/invoices/${id}`,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Invoice', id }],
    }),

    editInvoiceById: builder.mutation<
      InvoiceResponse,
      Omit<InvoiceResponse, 'createdDate' | 'updatedDate' | 'events'>
    >({
      query: ({ id, ...rest }) => ({
        url: `oapi/v1/invoices/${id}`,
        method: 'PUT',
        // Null invoice type on receivables create/update → receivables
        body: { ...rest, invoiceType: rest.invoiceType ?? 'receivables' },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Invoice', id },
        'Invoices',
      ],
    }),

    updateInvoiceStatus: builder.mutation<void, UpdateInvoiceStatusPayload>({
      query: ({ id, status }) => ({
        url: `oapi/v1/invoices/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Invoice', id },
        'Invoices',
        'CarrierInvoices',
      ],
    }),

    deleteInvoiceById: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `oapi/v1/invoices/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Invoice', id },
        'Invoices',
      ],
    }),

    addInvoiceEvent: builder.mutation<void, Omit<Event, 'createdDate'>>({
      query: ({ id, ...body }) => ({
        url: `oapi/v1/invoices/${id}/events`,
        method: 'POST',
        body,
      }),
    }),

    editInvoiceEvent: builder.mutation<void, Omit<Event, 'createdDate'>>({
      query: ({ id, ...body }) => ({
        url: `oapi/v1/invoices/${id}/events`,
        method: 'PUT',
        body,
      }),
    }),

    deleteInvoiceEvent: builder.mutation<void, DeleteEventPayload>({
      query: ({ invoiceId, eventId }) => ({
        url: `oapi/v1/invoices/${invoiceId}/events/${eventId}`,
        method: 'DELETE',
      }),
    }),

    addInvoiceDocument: builder.mutation<
      AddDocumentResponse,
      AddDocumentPayload
    >({
      query: ({ orderId, file, documentType }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `tapi/v1/docs/upload?document_type=${documentType}&persistence_id=${orderId}`,
          method: 'POST',
          body: formData,
        }
      },
    }),

    listInvoicedocuments: builder.query<
      ListDocumentsResponse,
      { orderId: string }
    >({
      query: ({ orderId }) => ({
        url: `tapi/v1/docs/order/${orderId}`,
      }),
      transformResponse: transformListInvoiceDocuments,
    }),

    getPreSignedUrl: builder.mutation<
      GetPreSignedUrlResponse,
      GetPreSignedUrlPayload
    >({
      query: (body) => ({
        url: `tapi/v1/docs/presigned-url`,
        method: 'POST',
        body,
      }),
    }),

    deleteInvoiceDocument: builder.mutation<
      { message: string },
      { s3Uri: string }
    >({
      query: (body) => ({
        url: `tapi/v1/docs/s3`,
        method: 'DELETE',
        body,
      }),
    }),

    sendInvoiceEmail: builder.mutation<any, SendInvoiceEmailPayload>({
      query: (body) => ({
        url: `oapi/v1/invoices/sendInvoiceEmail`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Invoices'],
    }),

    createBatch: builder.mutation<BatchResponse, CreateBatchPayload>({
      query: (body) => ({
        url: `oapi/v1/invoices/batch`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Invoices'],
    }),
    getBatchById: builder.query<BatchResponse, { batchId: number }>({
      query: ({ batchId }) => ({
        url: `oapi/v1/invoices/batch/${batchId}`,
      }),
    }),
    sendBatchInvoiceEmail: builder.mutation<any, SendBatchInvoiceEmailPayload>({
      query: (body) => ({
        url: `oapi/v1/invoices/batch/send-email`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Invoices'],
    }),

    getInvoiceEmails: builder.query<InvoiceEmailDto[], { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `oapi/v1/invoices/${invoiceId}/emails`,
      }),
      providesTags: (_result, _error, { invoiceId }) => [
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    addPaymentDocument: builder.mutation<
      AddDocumentResponse,
      AddDocumentPayload
    >({
      query: ({ orderId, file, documentType }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `tapi/v1/docs/payment/upload?document_type=${documentType}&persistence_id=${orderId}`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'PaymentDocuments', id: orderId },
      ],
    }),

    listPaymentDocuments: builder.query<
      ListDocumentsResponse,
      { orderId: string }
    >({
      query: ({ orderId }) => ({
        url: `tapi/v1/docs/payment/orders/${orderId}`,
      }),
      transformResponse: (response: ListDocumentsResponse) => {
        return {
          REMITTANCE: response.REMITTANCE ?? [],
          PAYMENT_RECEIPT: response.PAYMENT_RECEIPT ?? [],
          OTHER: response.OTHER ?? [],
        }
      },
      providesTags: (_result, _error, { orderId }) => [
        { type: 'PaymentDocuments', id: orderId },
      ],
    }),
  }),
})

export const {
  useListInvoicesByUserQuery,
  useLazyListInvoicesByUserQuery,
  useListCarrierInvoicesByStatusQuery,
  useGetCarrierInvoiceByIdQuery,
  useGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdQuery,
  useEditInvoiceByIdMutation,
  useUpdateInvoiceStatusMutation,
  useDeleteInvoiceByIdMutation,
  useAddInvoiceEventMutation,
  useEditInvoiceEventMutation,
  useDeleteInvoiceEventMutation,
  useAddInvoiceDocumentMutation,
  useListInvoicedocumentsQuery,
  useLazyListInvoicedocumentsQuery,
  useGetPreSignedUrlMutation,
  useDeleteInvoiceDocumentMutation,
  useSendInvoiceEmailMutation,
  useCreateBatchMutation,
  useLazyGetBatchByIdQuery,
  useSendBatchInvoiceEmailMutation,
  useGetInvoiceEmailsQuery,
  useAddPaymentDocumentMutation,
  useListPaymentDocumentsQuery,
  useLazyListPaymentDocumentsQuery,
} = invoicesApi
